import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  Button,
} from "@mui/material";
import {
  ThumbUp,
  ThumbDown,
  Feedback,
  Assignment,
  CheckCircle,
  Pending,
  TrendingUp,
} from "@mui/icons-material";
import { Get } from "../../../services/apiServices";
import { networkUrls } from "../../../services/networkurls";
import moment from "moment";
import ReuseableSnackbar from "../../../components/Snackbar/Snackbar";

// TypeScript interfaces matching the API response
interface FeedbackReportItem {
  id: number;
  user_id: string;
  user_email: string;
  user_name: string;
  message_text: string | null;
  feedback: string | null;
  dislike_reason: string | null;
  created_at: string;
}

interface DislikeReasonBreakdown {
  reason: string | null;
  count: number;
  percentage: number;
}

interface FeedbackStatistics {
  total_feedback: number;
  total_likes: number;
  total_dislikes: number;
  like_percentage: number;
  dislike_percentage: number;
  no_feedback_count: number;
}

interface FeedbackReportResponse {
  statistics: FeedbackStatistics;
  dislike_reasons: DislikeReasonBreakdown[];
  feedback_details: FeedbackReportItem[];
  date_range: { start_date: string; end_date: string } | null;
}

interface RaisedQueryReportItem {
  id: number;
  user_id: number;
  chat_id: number;
  question: string;
  reason: string | null;
  description: string | null;
  status: string;
  created_at: string;
}

interface QueryStatistics {
  total_queries: number;
  pending_queries: number;
  resolved_queries: number;
  pending_percentage: number;
  resolved_percentage: number;
}

interface RaisedQueryReportResponse {
  statistics: QueryStatistics;
  query_details: RaisedQueryReportItem[];
  date_range: { start_date: string; end_date: string } | null;
}

interface CombinedReportResponse {
  feedback_report: FeedbackReportResponse;
  raised_query_report: RaisedQueryReportResponse;
  generated_at: string;
}

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<CombinedReportResponse | null>(null);
  const [snackbarInfo, setSnackbarInfo] = useState({
    color: "",
    message: "",
  });
  const [isShowSnackbar, setIsShowSnackbar] = useState(false);

  useEffect(() => {
    fetchApplicationReport();
  }, []);

  const fetchApplicationReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Get(networkUrls.applicationReports, true);
      if (response && response.status === 200) {
        setReportData(response.data);
      } else {
        const errorMsg = response?.data?.detail || "Failed to fetch application report";
        setError(errorMsg);
        setSnackbarInfo({
          color: "error",
          message: errorMsg,
        });
        setIsShowSnackbar(true);
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || "An error occurred while fetching the report";
      setError(errorMsg);
      setSnackbarInfo({
        color: "error",
        message: errorMsg,
      });
      setIsShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({
    title,
    value,
    icon,
    color,
    subtitle,
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 2,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 16px ${color}20`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: "50%",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading application report...
        </Typography>
      </Box>
    );
  }

  if (error && !reportData) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box display="flex" justifyContent="center">
          <Button
            onClick={fetchApplicationReport}
            variant="contained"
            sx={{
              backgroundColor: "#4D8FAC",
              "&:hover": {
                backgroundColor: "#3d7a94",
              },
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!reportData) {
    return null;
  }

  const { feedback_report, raised_query_report, generated_at } = reportData;

  return (
    <Box sx={{ p: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Report Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generated at: {moment(generated_at).format("MMMM DD, YYYY [at] hh:mm A")}
        </Typography>
      </Box>

      {/* Feedback Statistics Cards */}
      <Typography variant="h5" fontWeight="600" mb={2} mt={4}>
        Feedback Statistics
      </Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Feedback"
            value={feedback_report.statistics.total_feedback}
            icon={<Feedback sx={{ fontSize: 32, color: "#4D8FAC" }} />}
            color="#4D8FAC"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Likes"
            value={feedback_report.statistics.total_likes}
            icon={<ThumbUp sx={{ fontSize: 32, color: "#2e7d32" }} />}
            color="#2e7d32"
            subtitle={`${feedback_report.statistics.like_percentage}% of feedback`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Dislikes"
            value={feedback_report.statistics.total_dislikes}
            icon={<ThumbDown sx={{ fontSize: 32, color: "#d32f2f" }} />}
            color="#d32f2f"
            subtitle={`${feedback_report.statistics.dislike_percentage}% of feedback`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="No Feedback"
            value={feedback_report.statistics.no_feedback_count}
            icon={<TrendingUp sx={{ fontSize: 32, color: "#ed6c02" }} />}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      {/* Dislike Reasons Breakdown */}
      {feedback_report.dislike_reasons.length > 0 && (
        <Card sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" mb={2}>
              Dislike Reasons Breakdown
            </Typography>
            <Grid container spacing={2}>
              {feedback_report.dislike_reasons.map((reason, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      borderLeft: "4px solid #d32f2f",
                    }}
                  >
                    <Typography variant="body2" fontWeight="600" gutterBottom>
                      {reason.reason || "No reason provided"}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Typography variant="h6" color="#d32f2f">
                        {reason.count}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({reason.percentage}%)
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Feedback Details Table */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={2}>
            Feedback Details
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell><strong> User Email</strong></TableCell>
                  <TableCell><strong>User Name</strong></TableCell>
                  <TableCell><strong>Message</strong></TableCell>
                  <TableCell><strong>Feedback</strong></TableCell>
                  <TableCell><strong>Dislike Reason</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedback_report.feedback_details.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No feedback data available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  feedback_report.feedback_details.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.user_email}</TableCell>
                      <TableCell>{item.user_name}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.message_text || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.feedback || "N/A"}
                          color={item.feedback?.toLowerCase() === "like" ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.dislike_reason || "N/A"}
                      </TableCell>
                      <TableCell>
                        {moment(item.created_at).format("MMM DD, YYYY HH:mm")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Query Statistics Cards */}
      <Typography variant="h5" fontWeight="600" mb={2}>
        Raised Query Statistics
      </Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Queries"
            value={raised_query_report.statistics.total_queries}
            icon={<Assignment sx={{ fontSize: 32, color: "#1976d2" }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Queries"
            value={raised_query_report.statistics.pending_queries}
            icon={<Pending sx={{ fontSize: 32, color: "#ed6c02" }} />}
            color="#ed6c02"
            subtitle={`${raised_query_report.statistics.pending_percentage}% of total`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Resolved Queries"
            value={raised_query_report.statistics.resolved_queries}
            icon={<CheckCircle sx={{ fontSize: 32, color: "#2e7d32" }} />}
            color="#2e7d32"
            subtitle={`${raised_query_report.statistics.resolved_percentage}% of total`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Resolution Rate"
            value={`${raised_query_report.statistics.resolved_percentage.toFixed(1)}%`}
            icon={<TrendingUp sx={{ fontSize: 32, color: "#4D8FAC" }} />}
            color="#4D8FAC"
          />
        </Grid>
      </Grid>

      {/* Query Details Table */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={2}>
            Raised Query Details
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>User ID</strong></TableCell>
                  <TableCell><strong>Chat ID</strong></TableCell>
                  <TableCell><strong>Question</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {raised_query_report.query_details.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No query data available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  raised_query_report.query_details.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.user_id}</TableCell>
                      <TableCell>{item.chat_id}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.question}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.reason || "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.description || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status || "N/A"}
                          color={
                            item.status?.toLowerCase() === "pending"
                              ? "warning"
                              : item.status?.toLowerCase() === "resolved" ||
                                item.status?.toLowerCase() === "closed" ||
                                item.status?.toLowerCase() === "completed"
                              ? "success"
                              : "default"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {moment(item.created_at).format("MMM DD, YYYY HH:mm")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          colour={snackbarInfo.color}
        />
      )}
    </Box>
  );
};

export default Reports;